import { useCallback, useEffect } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';

import { ChallengeStatus } from '@sg/graphql-schema';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useLoyaltyOfferOptInOnFocusMutation } from './graphql/LoyaltyOfferOptInOnFocus.generated';

// ─────────────────────────────────────────────────────────────────────────────

export function useOptInChallengeOnFocus() {
  // ─────────────────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();

  // ─── Router Params ───────────────────────────────────────────────────

  const params = useLocalSearchParams<OptInChallengeParams>();
  const router = useRouter();

  const [, optInChallenge] = useLoyaltyOfferOptInOnFocusMutation();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  const { challengeId, optIn } = params;

  // ─── Opt In Callback ─────────────────────────────────────────────────

  const handleOptInChallenge = useCallback(async () => {
    if (!isLoggedIn) return;

    if (!challengeId || optIn !== 'true') return;

    router.setParams({ challengeId: undefined, optIn: undefined });

    const { data } = await optInChallenge({ challengeId });

    const hasOptedIn =
      data?.challengeOptInV2?.__typename === 'Challenge' &&
      OPTED_IN_STATUSES.includes(data.challengeOptInV2.status);

    // ─── Notice Banners ──────────────────────────────────────────

    if (hasOptedIn) {
      addNoticeBanner({
        palette: 'success',
        text: formatMessage(messages.optInSuccess),
      });

      return;
    }

    addNoticeBanner({
      palette: 'caution',
      text: formatMessage(messages.optInFailure),
    });
  }, [isLoggedIn, challengeId, optIn, router, optInChallenge, addNoticeBanner, formatMessage]);

  // ─── Entrypoint ──────────────────────────────────────────────────────

  useEffect(() => {
    void handleOptInChallenge();
  }, [handleOptInChallenge]);
}

// ─── Constants ───────────────────────────────────────────────────────────────

// A successful opt-in can return a challenge that is already in progress or completed.
const OPTED_IN_STATUSES = [
  ChallengeStatus.Started,
  ChallengeStatus.InProgress,
  ChallengeStatus.Completed,
];

// ─── Types ───────────────────────────────────────────────────────────────────

type OptInChallengeParams = {
  challengeId?: string;
  optIn?: string;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  optInSuccess: {
    defaultMessage: 'You have successfully opted in to this challenge',
    description: 'Loyalty | Opt-in challenge on focus | Success message',
  },
  optInFailure: {
    defaultMessage: 'An error occurred while opting in to this challenge',
    description: 'Loyalty | Opt-in challenge on focus | Failure message',
  },
});
